import {
  TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
  sortReportRows,
} from "./renderConfig";
import { buildCsv } from "./agentServiceTimeExport";

const SUMMARY_HEADERS = Object.freeze([
  "Scope",
  "Branch",
  "Service",
  "Total",
  "Average",
  "Min",
  "Max",
  "Completed",
  "Completed %",
  "No Show",
  "No Show %",
  "Unattended",
  "Unattended %",
]);

const DAILY_HEADERS = Object.freeze([
  "Scope",
  "Branch",
  "Service",
  "Day",
  "Total",
  "Completed",
  "No Show",
  "Unattended",
]);

const toPrimitiveNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getPointDate = (point) => point.date ?? point.day ?? point.name ?? point.hour ?? "";
const getPoints = (row) => row.days || row.hours || [];
const pointLabelPrefix = (rows) =>
  rows.some((row) => getPoints(row).some((point) => point?.hour !== undefined && point?.hour !== null))
    ? "Hour"
    : "Day";

export const getTicketsPerServiceRows = ({ data = [], branchName = "", scope = "" }) =>
  (data || [])
    .filter((service) => toPrimitiveNumber(service?.total))
    .map((service) => ({
      ...service,
      serviceName: service.name || "",
      branchName,
      scope,
      average: service.averageTicketsPerDay ?? service.averageTicketsPerHour ?? service.average,
      min: service.minTicketsPerDay ?? service.minTicketsPerHour ?? service.min,
      max: service.maxTicketsPerDay ?? service.maxTicketsPerHour ?? service.max,
    }));

export const getTicketsPerServiceRowsForScope = (input, renderConfig) => {
  const config = normalizeRenderConfig(renderConfig, {
    columnDefinitions: TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  return sortReportRows(getTicketsPerServiceRows(input), config, {
    columnDefinitions: TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  });
};

export const getTicketsPerServiceSummaryCsvRows = (scopes = [], renderConfig) =>
  scopes.flatMap((scope) =>
    getTicketsPerServiceRowsForScope(scope, renderConfig).map((row) => [
      row.scope || "",
      row.branchName || "",
      row.serviceName || "",
      toPrimitiveNumber(row.total),
      toPrimitiveNumber(row.average),
      toPrimitiveNumber(row.min),
      toPrimitiveNumber(row.max),
      toPrimitiveNumber(row.completed),
      toPrimitiveNumber(row.pctCompleted),
      toPrimitiveNumber(row.noShow),
      toPrimitiveNumber(row.pctNoShow),
      toPrimitiveNumber(row.unattended),
      toPrimitiveNumber(row.pctUnattended),
    ])
  );

export const getTicketsPerServiceDailyCsvRows = (scopes = [], renderConfig) =>
  scopes.flatMap((scope) =>
    getTicketsPerServiceRowsForScope(scope, renderConfig).flatMap((row) =>
      getPoints(row).map((point) => [
        row.scope || "",
        row.branchName || "",
        row.serviceName || "",
        getPointDate(point),
        toPrimitiveNumber(point.total),
        toPrimitiveNumber(point.completed),
        toPrimitiveNumber(point.no_show),
        toPrimitiveNumber(point.unattended),
      ])
    )
  );

const getDayLabels = (rows) =>
  Array.from(
    new Set(
      rows.flatMap((row) =>
        getPoints(row).map(getPointDate).filter((date) => date !== undefined && date !== null && date !== "")
      )
    )
  );

export const getTicketsPerServiceMergedCsvHeaders = (scopes = [], renderConfig) => {
  const rows = scopes.flatMap((scope) => getTicketsPerServiceRowsForScope(scope, renderConfig));
  const labels = getDayLabels(rows);
  const prefix = pointLabelPrefix(rows);
  return [...SUMMARY_HEADERS, ...labels.map((day) => `${prefix} ${day}`)];
};

export const getTicketsPerServiceMergedCsvRows = (scopes = [], renderConfig) => {
  const rows = scopes.flatMap((scope) => getTicketsPerServiceRowsForScope(scope, renderConfig));
  const dayLabels = getDayLabels(rows);
  return rows.map((row) => {
    const valuesByDay = Object.fromEntries(
      getPoints(row).map((point) => [getPointDate(point), toPrimitiveNumber(point.total)])
    );
    return [
      row.scope || "",
      row.branchName || "",
      row.serviceName || "",
      toPrimitiveNumber(row.total),
      toPrimitiveNumber(row.average),
      toPrimitiveNumber(row.min),
      toPrimitiveNumber(row.max),
      toPrimitiveNumber(row.completed),
      toPrimitiveNumber(row.pctCompleted),
      toPrimitiveNumber(row.noShow),
      toPrimitiveNumber(row.pctNoShow),
      toPrimitiveNumber(row.unattended),
      toPrimitiveNumber(row.pctUnattended),
      ...dayLabels.map((day) => valuesByDay[day] ?? ""),
    ];
  });
};

const triggerCsvDownload = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const downloadTicketsPerServiceCsvFiles = ({ scopes, renderConfig, filenamePrefix }) => {
  triggerCsvDownload(
    `${filenamePrefix}.csv`,
    buildCsv(
      getTicketsPerServiceMergedCsvHeaders(scopes, renderConfig),
      getTicketsPerServiceMergedCsvRows(scopes, renderConfig)
    )
  );
};
